import React, { useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  TextField,
  Typography
} from "@mui/material";

interface SaveTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (name: string, description?: string) => Promise<void>;
  onUpdate?: (id: number, name: string, description?: string) => Promise<void>;
  saving: boolean;
  currentTemplateId?: number | null;
  currentTemplateName?: string;
  currentTemplateDescription?: string;
}

export const SaveTemplateDialog: React.FC<SaveTemplateDialogProps> = ({
  open,
  onClose,
  onSave,
  onUpdate,
  saving,
  currentTemplateId,
  currentTemplateName,
  currentTemplateDescription
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saveAsNew, setSaveAsNew] = useState(false);

  const isUpdate = currentTemplateId && !saveAsNew;

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setName(currentTemplateName || "");
      setDescription(currentTemplateDescription || "");
      setSaveAsNew(false);
    }
  }, [open, currentTemplateName, currentTemplateDescription]);

  const handleSave = async () => {
    if (!name.trim()) return;

    if (isUpdate && onUpdate) {
      await onUpdate(currentTemplateId, name.trim(), description.trim() || undefined);
    } else {
      await onSave(name.trim(), description.trim() || undefined);
    }
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: "8px" } } }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {isUpdate ? "Update Template" : "Save as Template"}
        <IconButton onClick={handleClose} size="sm" disabled={saving}>
          <CloseIcon fontSize="sm" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: "16px !important" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <TextField
            label="Template Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            autoFocus
            placeholder="Enter template name"
            size="small"
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="Add a description for this template"
            size="small"
          />
          <Typography variant="caption" color="textSecondary">
            The template will save the report layout, card positions, and configurations. Data will be refreshed when
            the template is loaded.
          </Typography>
          {currentTemplateId && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {saveAsNew ? (
                <>
                  Creating a new template.{" "}
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => {
                      setSaveAsNew(false);
                      setName(currentTemplateName || "");
                      setDescription(currentTemplateDescription || "");
                    }}
                    sx={{ verticalAlign: "baseline" }}
                  >
                    Update existing instead?
                  </Link>
                </>
              ) : (
                <>
                  Updating existing template.{" "}
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => {
                      setSaveAsNew(true);
                      setName("");
                      setDescription("");
                    }}
                    sx={{ verticalAlign: "baseline" }}
                  >
                    Save as new instead?
                  </Link>
                </>
              )}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!name.trim() || saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? "Saving..." : isUpdate ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
